import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import TutorialModal from '../components/TutorialModal';
import { hasSeenTutorial } from '../utils/tutorialData';
import {
  TeamIcon as Users,
  MailIcon as Mail,
  SearchIcon as Search,
  ShieldIcon as Shield,
  ArrowRightIcon as ArrowRight
} from '../components/Icons';
import UnifiedLoader from '../components/UnifiedLoader';

const AdminUsers = () => {
  const { API_URL, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showTutorial, setShowTutorial] = useState(!hasSeenTutorial('admin-users'));
  const [remindLoading, setRemindLoading] = useState(false);

  const handleRemindPhotos = async () => {
    if (!window.confirm('Möchtest du wirklich alle Teammitglieder ohne Profilbild per E-Mail erinnern?')) return;
    
    try {
      setRemindLoading(true);
      const config = {
        headers: { Authorization: `Bearer ${user.token}` },
      };
      const { data } = await axios.post(`${API_URL}/users/remind-photos`, {}, config);
      alert(data.message);
    } catch (error) {
      console.error('Fehler beim Senden der Erinnerungen:', error);
      alert('Fehler beim Senden der Erinnerungen.');
    } finally {
      setRemindLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${user.token}` },
      };

      if (user.role === 'admin') {
        const { data } = await axios.get(`${API_URL}/users`, config);
        setUsers(data);
      } else {
        navigate('/');
        return;
      }
      setLoading(false);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchUsers();
    } else if (user) {
      navigate('/profile');
    }
  }, [user, API_URL]);

  const filteredUsers = users.filter(u => {
    const searchString = `${u.userData?.firstName || ''} ${u.userData?.lastName || ''} ${u.userData?.scoutName || ''} ${u.email}`.toLowerCase();
    const matchesSearch = searchString.includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  if (loading) return <UnifiedLoader fullscreen />;

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-7xl animate-fade-in">
      {showTutorial && (
        <TutorialModal pageId="admin-users" onClose={() => setShowTutorial(false)} />
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
        <div>
          <div className="flex items-center gap-3 text-primary mb-2">
            <Users sx={{ fontSize: 32 }} />
            <h1 className="text-display-small font-black tracking-tight">Benutzerverwaltung</h1>
          </div>
          <p className="text-body-large text-surface-onVariant">Verwalte die Mitglieder und Rollen deines Stammes.</p>
        </div>

        <button
          onClick={handleRemindPhotos}
          disabled={remindLoading}
          className="md3-btn-tonal gap-2 py-3 px-6 !rounded-2xl"
        >
          {remindLoading ? (
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Mail sx={{ fontSize: 20 }} />
              <span>Foto-Reminder senden</span>
            </>
          )}
        </button>
      </div>


      <div className="md3-card-elevated p-4 md:p-6 mb-8 rounded-3xl border border-outlineVariant/30 bg-surface-containerLowest shadow-sm flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search sx={{ fontSize: 20 }} className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-onVariant opacity-50" />
          <input
            type="text"
            placeholder="Name oder E-Mail suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-2xl bg-surface-containerHigh border-none focus:ring-2 focus:ring-primary/20 text-surface-on"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-4 py-3 rounded-2xl bg-surface-containerHigh border-none focus:ring-2 focus:ring-primary/20 text-surface-on font-bold min-w-[150px]"
        >
          <option value="all">Alle Rollen</option>
          <option value="admin">Admins</option>
          <option value="gruppenleiter">Gruppenleiter</option>
          <option value="eltern">Eltern</option>
          <option value="kind">Kinder</option>
        </select>
      </div>

      <div className="md3-card-elevated overflow-hidden rounded-[32px] border border-outlineVariant/30 bg-surface-containerLowest shadow-sm">
        {filteredUsers.length > 0 ? (
          filteredUsers.map((u) => (
            <div
              key={u._id}
              onClick={() => navigate(`/users/${u._id}`)}
              className="p-4 md:px-6 md:py-5 flex items-center justify-between border-b border-outlineVariant/20 hover:bg-surface-on/5 cursor-pointer transition-all active:scale-[0.99] last:border-0 group"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-full bg-primary-container text-primary-onContainer flex items-center justify-center font-black text-lg shadow-inner shrink-0 group-hover:scale-110 transition-all overflow-hidden border border-outlineVariant/20">
                  {u.userData?.profilePhoto?.url || (u.userData?.photos && u.userData.photos[0]?.url) || u.avatarUrl || u.userData?.avatarConfig?.modelUrl ? (
                    <img 
                      src={u.userData?.profilePhoto?.url || (u.userData?.photos && u.userData.photos[0]?.url) || u.avatarUrl || u.userData?.avatarConfig?.modelUrl} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    (u.userData?.scoutName || u.userData?.firstName || u.email).charAt(0).toUpperCase()
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-title-medium font-bold text-surface-on truncate">
                    {u.userData?.scoutName || `${u.userData?.firstName || ''} ${u.userData?.lastName || ''}`.trim() || u.email}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5 opacity-60">
                    <Mail sx={{ fontSize: 14 }} className="shrink-0" />
                    <p className="text-body-small truncate italic">{u.email}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 md:gap-8">
                <div className="hidden sm:flex flex-col items-end">
                  <div className="flex items-center gap-1.5 mb-1">
                    {u.role === 'admin' && <Shield sx={{ fontSize: 14, color: 'var(--md-sys-color-primary)' }} />}
                    <span className="text-label-small font-black uppercase tracking-widest text-surface-onVariant">
                      {u.role}
                    </span>
                  </div>

                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-surface-containerHigh text-primary group-hover:bg-primary group-hover:text-primary-on transition-colors">
                  <ArrowRight sx={{ fontSize: 18 }} />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-12 text-center text-surface-onVariant opacity-50 flex flex-col items-center gap-4">
            <Search sx={{ fontSize: 48 }} />
            <p className="text-title-medium font-medium">Keine Benutzer gefunden</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
